import {ICellRendererParams} from "@ag-grid-community/core";
import {Button, Intent, Text, Tooltip} from "@blueprintjs/core";
import {MaybeElement} from "@blueprintjs/core/src/common/props";
import {IconName} from "@blueprintjs/icons";
import styled from "@emotion/styled";
import _ from "lodash";
import {CSSProperties, useContext, useMemo} from "react";
import {getStopAddressText} from "../../../utils/General";
import {deliveryStop, jobStopStatusToString, pickupStop} from "../../../utils/stops/StopUtils";
import {EmptyValueStrings, TenantPreferences} from "../../../constants/constants";
import {AuthContext, AuthState} from "../../../providers/AuthProvider";
import DispatchNotes from "../dispatch-notes/DispatchNotes";
import {AgJob} from "../../job/JobPanel";
import {extractSimplePref, PreferenceContext} from "../../../providers/PreferenceProvider";

type ButtonProps = {
  text?: string;
  onClick(event: MouseEvent, data: any, authState: AuthState): void;
  intent?: Intent;
  icon?: IconName | MaybeElement;
  minimal?: boolean;
  testIdPrefix?: string;
  dispatchNotesEnabled?: boolean;
};

export enum JobNumberTooltipUsage {
  UNASSIGNED_JOBS_GRID = "unassignedJobsGrid",
  MANIFEST_DETAILS_GRID = "manifestDetailsGrid",
  ASSIGNED_STOPS_GRID = "assignedStopsGrid"
}

export type ContentItemType = {key: string; title: string; value: any; path?: string};

const CellContainer = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
`;

const Container = styled.div`
  font-size: 14px;
  font-family: "Roboto", sans-serif;
  font-weight: 400;
  color: #161616;
  background-color: #ffffff;
  max-height: 95vh;
  overflow-y: auto;
`;
const ItemContainer = styled.div`
  display: flex;
  line-height: 22px;
`;
const ItemKey = styled(Text)`
  text-transform: uppercase;
  font-weight: 500;
  width: 160px;
`;
const ItemValue = styled(Text)`
  flex: 1;
  padding-right: 2px;
`;

const CUSTOM_TITLE_FIELDS: {key: string; customTitle: string}[] = [
  {key: "SVC Type", customTitle: "svt"},
  {key: "SVT", customTitle: "svt"},
  {key: "Customer Account", customTitle: "cust. account"},
  {key: "Customer Name", customTitle: "cust. name"},
  {key: "Customer Notes", customTitle: "cust. notes"},
  {key: "Customer Alias", customTitle: "cust. alias"},
  {key: "Total Mileage", customTitle: "ttl mileage"},
  {key: "Order Notes", customTitle: "notes"}
];

const COMBINE_VALUE_FIELDS: string[] = [
  "Address",
  "ManifestJobAddress",
  "Time",
  "Scheduled Time",
  "Completed Time",
  "POD Time",
  "POD Date",
  "Late Time",
  "Early Time",
  "Arrival Time",
  "Departure Time",
  "ETA",
  "ETA Generation Date/Time"
];

const HIDE_FIELDS: string[] = ["Color", "Details", "#"];

export const getTitleByKey = (key: string) => {
  const customTitle = CUSTOM_TITLE_FIELDS.find((c) => c.key === key);
  if (customTitle) {
    return customTitle.customTitle;
  }
  return key;
};

export const getDisplayValue = (item: ContentItemType) => {
  if (_.isNumber(item.value) || _.isBoolean(item.value)) {
    return item.value.toString();
  }

  if (!item.value) {
    return "";
  }

  if (_.isString(item.value) && item.key !== "Status") {
    return item.value.length > 120 ? item.value.substring(0, 120) + "..." : item.value;
  }

  switch (item.key) {
    case "Job #": {
      return item.value?.text ?? "";
    }

    case "Status": {
      return jobStopStatusToString(item.value) ?? EmptyValueStrings.notProvided;
    }

    case "Zone":
    case "Time": {
      return (
        <div>
          <span>{_.get(item.value, "upperField.value") ?? EmptyValueStrings.notProvided}</span>
          <span> / </span>
          <span style={{color: "#797979"}}>
            {_.get(item.value, "lowerField.value") ?? EmptyValueStrings.notProvided}
          </span>
        </div>
      );
    }

    case "Address": {
      return (
        <div>
          <span>{_.get(item.value, "upperField.value") ?? EmptyValueStrings.notProvided}</span>
          <span> / </span>
          <br />
          <span style={{color: "#797979"}}>
            {_.get(item.value, "lowerField.value") ?? EmptyValueStrings.notProvided}
          </span>
        </div>
      );
    }

    case "ManifestJobAddress": {
      return (
        <div>
          <span>{_.get(item.value, "upperField.value") ?? EmptyValueStrings.notProvided}</span>
          <br />
          <span style={{color: "#797979"}}>
            {_.get(item.value, "lowerField.value") ?? EmptyValueStrings.notProvided}
          </span>
        </div>
      );
    }

    case "Ready":
    case "Ready At":
    case "Due By":
    case "Order Placed":
    case "Scheduled Time":
    case "POD Time":
    case "POD Date":
    case "Completed Time":
    case "Late Time":
    case "Early Time":
    case "Arrival Time":
    case "Departure Time":
    case "ETA":
    case "ETA Generation Date/Time":
    case "ETA Notification Window Sent": {
      if (!_.get(item.value, "upperField.value") && !_.get(item.value, "lowerField.value")) {
        return "";
      }
      return (
        <div>
          {_.get(item.value, "upperField.value")} {_.get(item.value, "lowerField.value")}
        </div>
      );
    }

    default:
      return item.value;
  }
};

const ContentItem = ({item}: {item: ContentItemType}) => {
  const styling: CSSProperties = {};

  if (/Notes/.exec(item.key) && item.value) {
    styling.color = "#797979";
    styling.fontStyle = "italic";
    styling.maxWidth = "350px";
  }

  if (/SVC Type/.exec(item.key) || /Job #/.exec(item.key) || /SVT/.exec(item.key)) {
    styling.fontWeight = 500;
  }

  const displayValue = getDisplayValue(item) || EmptyValueStrings.notProvided;

  return (
    <ItemContainer data-testid="job-number-tooltip-conten-item">
      <ItemKey>{item.title}:</ItemKey>
      <ItemValue style={styling}>{displayValue}</ItemValue>
    </ItemContainer>
  );
};

export const JobnumberTooltip = ({
  data,
  usage = JobNumberTooltipUsage.UNASSIGNED_JOBS_GRID
}: {
  data: ICellRendererParams<any>;
  usage?: JobNumberTooltipUsage;
}) => {
  const rowNode = useMemo(() => data.api.getDisplayedRowAtIndex(data.rowIndex), [data.api, data.rowIndex]);
  const displayedFields: ContentItemType[] = data.columnApi
    .getAllDisplayedColumns()
    .filter((c) => !c.getUserProvidedColDef()?.suppressColumnsToolPanel)
    .map((c) => {
      const columnId = c.getColId();
      const columnName = c.getUserProvidedColDef()?.headerName ?? c.getColDef()?.headerName ?? "";
      let customColumnNameForManifestJobGrid: string | undefined = undefined;
      if (usage !== JobNumberTooltipUsage.UNASSIGNED_JOBS_GRID) {
        switch (columnName) {
          case "Address":
            customColumnNameForManifestJobGrid = "ManifestJobAddress";
            break;
          case "Zone":
            customColumnNameForManifestJobGrid = "ManifestJobAddressZone";
            break;
          default:
            customColumnNameForManifestJobGrid = undefined;
        }
      }
      return {
        key: customColumnNameForManifestJobGrid ?? columnName,
        path: columnId,
        title: getTitleByKey(columnName),
        value: data.api.getValue(columnId, rowNode!)
      };
    });

  const sortedDisplayedFields = _.sortBy(displayedFields, ({key}) => {
    return /Order Notes/.exec(key) ? 1 : 0;
  });
  COMBINE_VALUE_FIELDS.forEach((f) => {
    const combineFieldIndex = sortedDisplayedFields.findIndex((df) => df.key === f);
    const updatedField = {...sortedDisplayedFields[combineFieldIndex]};

    if (combineFieldIndex && f === "Address" && usage === JobNumberTooltipUsage.UNASSIGNED_JOBS_GRID) {
      const pickup = pickupStop(rowNode?.data?.stops);
      const delivery = deliveryStop(rowNode?.data?.stops);
      const pickupAddress = pickup ? getStopAddressText({stop: pickup, hasDispatchZone: true}) : "";
      const deliveryAddress = delivery ? getStopAddressText({stop: delivery, hasDispatchZone: true}) : "";
      _.set(updatedField, "value.upperField.value", pickupAddress);
      _.set(updatedField, "value.lowerField.value", deliveryAddress);
      sortedDisplayedFields[combineFieldIndex] = updatedField;
    }
  });

  const fields = sortedDisplayedFields.filter((f) => !HIDE_FIELDS.includes(f.key));

  return (
    <Container data-testid="job-number-tooltip">
      {fields.map((c) => (
        <ContentItem key={c.key} item={c} />
      ))}
    </Container>
  );
};

const JobNumberCellRenderer = (props: ICellRendererParams<AgJob, ButtonProps>) => {
  const authState = useContext(AuthContext);
  const {tenantPreferences} = useContext(PreferenceContext);
  const timezoneOfJobStop = extractSimplePref(tenantPreferences, TenantPreferences.timezoneOfJobStop, false).value;
  const firstStop = [...(props.data?.stops ?? [])].sort((a, b) => a.sequence - b.sequence)[0];

  return (
    <CellContainer>
      <div style={{flexShrink: 1, overflow: "hidden"}}>
        <Tooltip
          popoverClassName="job-number-tooltip-popover"
          content={<JobnumberTooltip data={props} />}
          position="auto"
          interactionKind="hover"
          hoverCloseDelay={100}
          targetProps={{style: {width: "100%"}}}
        >
          <Button
            data-testid={`${props.value.testIdPrefix ? props.value.testIdPrefix : "button-cell"}-${props.node.id}`}
            text={props.value.text}
            intent={props.value.intent}
            variant={props.value.minimal ? "minimal" : "solid"}
            icon={props.value.icon}
            ref={(ref) => {
              if (!ref) return;
              ref!.onclick = (e: MouseEvent) => {
                e.stopPropagation();
                props.value.onClick(e, props.data, authState);
              };
            }}
            style={{fontSize: "12px", padding: "2px", width: "100%"}}
            ellipsizeText
          />
        </Tooltip>
      </div>
      {props.value.dispatchNotesEnabled && (
        <div style={{flexShrink: 0, marginLeft: "2px", marginTop: "-2px"}}>
          <DispatchNotes
            identifier={`job-${props.data?.jobId ?? 0}`}
            orderId={props.data?.order?.orderId ?? 0}
            orderNumber={props.data?.jobNumber ?? ""}
            orderEvents={props.data?.order?.orderEvents ?? []}
            timeZoneOfJobStop={timezoneOfJobStop}
            firstStopTimezone={firstStop?.timeZone}
          />
        </div>
      )}
    </CellContainer>
  );
};

export default JobNumberCellRenderer;
